<!-- 
Chips containing warnings.

These are used on the levy collection provider and invoice screens to show warnings. 
Hovering over the chip will display a tooltip with further details of the warning.
-->
<template>
  <v-layout v-if="show" grid>
    <v-flex shrink class="ma-3 hidden-sm-and-down">
      <h4 class="subheading" style="white-space:nowrap;">{{ title }}:</h4>
    </v-flex>
    <v-flex grow class="ma-2">
      <v-tooltip v-for="warning in warnings" :key="warning.type" max-width="500" bottom>
        <v-chip 
          slot="activator"
          :close="canDismiss(warning)"
          :color="getChipColour(warning)"
          @input="dismissWarning"
        >
          <v-avatar v-if="warning.forInformationOnly" class="hidden-xs-only">
            <v-icon>info</v-icon>
          </v-avatar>
          <v-avatar v-else class="hidden-xs-only">
            <v-icon color="error">error_outline</v-icon>
          </v-avatar>
          <strong class="mr-2">{{ lookups.levyCollectionProviderWarningTypeDescriptions[warning.type] }}{{ (warning.description ? ":" : ".") }}</strong>
          <span>{{ warning.description | fullStop }}</span>
        </v-chip>
        <p v-for="(line, i) in getTooltipLines(warning)" :key="`line-${i}`" class="my-0">{{ line }}</p>
      </v-tooltip>
    </v-flex>
  </v-layout>
</template>

<script>
import _ from "underscore";
import lookupMixins from "@/mixins/lookup.mixins";
import filterMixins from "@/mixins/filter.mixins";
import * as action from "@/store/levy-collection-providers/action-types";
import * as claim from "@/store/claim-types";

export default {
  mixins: [lookupMixins, filterMixins],
  props: {
    levyCollectionId: {
      validator: prop => typeof prop === "number" || prop == null,
      default: null
    },
    providerId: {
      validator: prop => typeof prop === "number" || prop == null,
      default: null
    },
    warnings: {
      type: Array,
      required: true
    },
    filter: {
      type: Array,
      default: null
    }
  },
  computed: {
    show() {
      return this.filteredWarnings.length > 0;
    },
    filteredWarnings() {
      if (this.filter && this.filter.length) {
        return _(this.warnings).filter(w => this.filter.includes(w.type));
      }
      return this.warnings;
    },
    processing() {
      return this.$store.state.levyCollectionProviders.dismissWarning.processing;
    },
    title() {
      const hasInfo = _(this.filteredWarnings).some(w => w.forInformationOnly);
      const hasWarning = _(this.filteredWarnings).some(w => !w.forInformationOnly);
      if (hasInfo && hasWarning) return "Warnings and flags";
      return hasInfo ? "Flags" : "Warnings";
    },
    hasDismissPermission() {
      return this.$store.getters.claims.includes(claim.SAP_DISMISS_RECONCILIATION_WARNING);
    }
  },
  methods: {
    canDismiss(warning) {
      return (
        this.levyCollectionId &&
        this.providerId &&
        !this.processing &&
        warning.type == "SapReconciliationWarning" &&
        this.hasDismissPermission &&
        this.doesLookupHaveAttribute(
          warning.sapReconciliationWarningType,
          "CanBeDismissed",
          this.lookups.sapReconciliationWarningTypes
        )
      );
    },
    dismissWarning() {
      this.$store.dispatch(action.DISMISS_SAP_RECONCILIATION_WARNING, {
        levyCollectionId: this.levyCollectionId,
        providerId: this.providerId
      });
    },
    getChipColour(warning) {
      return warning.forInformationOnly ? "blue lighten-3" : "red lighten-4";
    },
    getTooltipLines(warning) {
      const hasLongDescription = this.doesLookupHaveAttribute(
        warning.type,
        "LongDescription",
        this.lookups.levyCollectionProviderWarningTypes
      );
      if (hasLongDescription) {
        const longDescription = _(this.lookups.levyCollectionProviderWarningTypes).find(t => t.value == warning.type)
          .attributeData.longDescription.longDescription;
        return longDescription.split("\n");
      }
      return [this.lookups.levyCollectionProviderWarningTypeDescriptions[warning.type]];
    }
  }
};
</script>
